import axios from '../axios';
import { Category, SubCategory, Use, Materiality } from '../types/projectDocumentation.ts';

const getAuthHeaders = () => {
  const authToken = localStorage.getItem("access-Token-Edify");
  return { headers: { Authorization: `Bearer ${authToken}` } };
};

export const projectDocumentationService = {
  getAllCategories: async (): Promise<Category[]> => {
    try {
      const response = await axios.get('/preliminary-project/category/all', getAuthHeaders());
      return response.data.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  },

  getSubCategoriesByCategoryId: async (categoryId: string): Promise<SubCategory[]> => {
    try {
      const response = await axios.get(`/preliminary-project/sub-categories/${categoryId}`, getAuthHeaders());
      return response.data.data;
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      throw error;
    }
  },

  getAllSubCategories: async (categories: Category[]): Promise<{ [categoryId: string]: SubCategory[] }> => {
    try {
      const subCategoriesMap: { [categoryId: string]: SubCategory[] } = {};
      
      const promises = categories.map(async (category) => {
        try {
          const subCategories = await projectDocumentationService.getSubCategoriesByCategoryId(category.id);
          subCategoriesMap[category.id] = subCategories;
        } catch (error) {
          console.error(`Error fetching subcategories for category ${category.id}:`, error);
          subCategoriesMap[category.id] = []; 
        }
      });

      await Promise.all(promises);
      return subCategoriesMap;
    } catch (error) {
      console.error('Error fetching all subcategories:', error);
      throw error;
    }
  },

  getAllUses: async (): Promise<Use[]> => {
    try {
      const response = await axios.get('/preliminary-project/use/all', getAuthHeaders());
      return response.data.data;
    } catch (error) {
      console.error('Error fetching uses:', error);
      throw error;
    }
  },

  getAllMaterialities: async (): Promise<Materiality[]> => {
    try {
      const response = await axios.get('/preliminary-project/materiality/all', getAuthHeaders());
      return response.data.data;
    } catch (error) {
      console.error('Error fetching materialities:', error);
      throw error;
    }
  },

  getAllFileTags: async (): Promise<any[]> => {
    try {
      const response = await axios.get('/preliminary-project/tag/all', getAuthHeaders());
      return response.data.data;
    } catch (error) {
      console.error('Error fetching file tags:', error);
      throw error;
    }
  },

  createProjectDocumentation: async (payload: any): Promise<any> => {
    try {
      const response = await axios.post('/preliminary-project/create', payload, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error creating project documentation:', error);
      throw error;
    }
  },


  uploadProjectDocumentationFiles: async (projectId: number, items: any[], onProgress?: (current: number, total: number) => void): Promise<{ results: any[]; failures: any[] }> => {
    try {
      const validItems = items.filter(item => !(item.isSerialized && item.type === 'file'));
      const total = validItems.length;
      const results: any[] = [];
      const failures: any[] = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        if (item.isSerialized && item.type === 'file') {
          failures.push({ id: item.id, name: item.fileName || item.name, tagId: item.tagId || '', reason: 'missing_file_after_oauth' });
          continue;
        }
        
        const formData = new FormData();
        
        if (item.type === 'file' && item.file) {
          formData.append('file', item.file);
          formData.append('fileTag', item.tagId || '');
          formData.append('link', '');
          formData.append('linkTag', '');
        } else if (item.type === 'link' && item.link) {
          formData.append('link', item.link);
          formData.append('linkTag', item.tagId || '');
          formData.append('file', '');
          formData.append('fileTag', '');
        }

        try {
          const authHeaders = getAuthHeaders();
          const response = await axios.post(
            `/preliminary-project/file/upload/${projectId}`, 
            formData,
            {
              ...authHeaders,
              headers: {
                ...authHeaders.headers,
                'Content-Type': 'multipart/form-data',
              },
            }
          );
          results.push(response.data);
        } catch (e: any) {
          const status = e?.response?.status;
          let reason = 'upload_error';
          if (status === 400) reason = 'invalid_request';
          if (status === 413) reason = 'file_too_large';
          if (status === 415 || status === 422) reason = 'unsupported_format';
          failures.push({ id: item.id, name: item.name, tagId: item.tagId || '', reason });
        }
        
        if (onProgress) {
          const processedCount = results.length;
          onProgress(processedCount, total);
        }
      }

      return { results, failures };
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    }
  },

  createProjectDocumentationComplete: async (payload: any, items: any[], onProgress?: (current: number, total: number) => void): Promise<{ data: any; failures: any[] }> => {
    try {
      const createResponse = await projectDocumentationService.createProjectDocumentation(payload);
      const projectId = createResponse.data.id;

      if (items && items.length > 0) {
        const { failures } = await projectDocumentationService.uploadProjectDocumentationFiles(projectId, items, onProgress);
        return { data: createResponse.data, failures };
      }
      return { data: createResponse.data, failures: [] };
    } catch (error) {
      console.error('Error creating complete project documentation:', error);
      throw error;
    }
  },

  updateProjectDocumentation: async (id: string, payload: any): Promise<any> => {
    try {
      const authHeaders = getAuthHeaders();
      const response = await axios.patch(`/preliminary-project/${id}`, payload, {
        ...authHeaders,
        headers: {
          ...authHeaders.headers,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating project documentation:', error);
      throw error;
    }
  },

  getProjectDocumentationById: async (id: string): Promise<any> => {
    try {
      const response = await axios.get(`/preliminary-project/${id}`, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error fetching project documentation:', error);
      throw error;
    }
  },

  getPendingProjectDocumentations: async (page: number = 1, limit: number = 9, filters?: any): Promise<any> => {
    try {
      const response = await axios.get('/preliminary-project/admin/all', getAuthHeaders());
      
      let data = response.data.data || [];
      
      if (filters?.status && filters.status !== 'all') {
        if (filters.status === 'sin-estado') {
          data = data.filter((item: any) => !item.state || item.state === '');
        } else if (filters.status === 'pending') {
          data = data.filter((item: any) => item.state === 'pending');
        } else if (filters.status === 'approved') {
          data = data.filter((item: any) => item.state === 'approved');
        } else if (filters.status === 'refused') {
          data = data.filter((item: any) => item.state === 'refused');
        } else {
          data = data.filter((item: any) => item.state === filters.status);
        }
      }
      
      if (filters?.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        data = data.filter((item: any) => 
          item.name?.toLowerCase().includes(searchLower) ||
          item.description?.toLowerCase().includes(searchLower)
        );
      }
      
      if (filters?.minBudget) {
        data = data.filter((item: any) => parseFloat(item.budget) >= filters.minBudget);
      }
      
      if (filters?.maxBudget) {
        data = data.filter((item: any) => parseFloat(item.budget) <= filters.maxBudget);
      }
      
      const total = data.length;
      const totalPages = Math.ceil(total / limit);
      const startIndex = (page - 1) * limit;
      const paginatedData = data.slice(startIndex, startIndex + limit);
      
      return {
        data: paginatedData,
        total,
        page,
        limit,
        totalPages
      };
    } catch (error) {
      console.error('Error fetching pending project documentations:', error);
      throw error;
    }
  },

  approveProjectDocumentation: async (id: string, adminNote?: string): Promise<any> => {
    try {
      const response = await axios.patch(
        `/preliminary-project/admin/${id}`,
        { state: 'approved' },
        getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error approving project documentation:', error);
      throw error;
    }
  },

  rejectProjectDocumentation: async (id: string, reason: string): Promise<any> => {
    try {
      const response = await axios.patch(
        `/preliminary-project/admin/${id}`,
        { state: 'refused' },
        getAuthHeaders()
      );
      return response.data;
    } catch (error) {
      console.error('Error rejecting project documentation:', error);
      throw error;
    }
  },

  getProjectDocumentationStats: async (): Promise<any> => {
    try {
      const response = await axios.get('/preliminary-project/admin/all', getAuthHeaders());
      const data = response.data.data || [];
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const noStateCount = data.filter((p: any) => !p.state || p.state === '').length;
      const pendingCount = data.filter((p: any) => p.state === 'pending').length;
      
      const approvedToday = data.filter((p: any) => {
        if (p.state !== 'approved') return false;
        const updatedDate = new Date(p.updated_at);
        updatedDate.setHours(0, 0, 0, 0);
        return updatedDate.getTime() === today.getTime();
      }).length;
      const rejectedToday = data.filter((p: any) => {
        if (p.state !== 'refused') return false;
        const updatedDate = new Date(p.updated_at);
        updatedDate.setHours(0, 0, 0, 0);
        return updatedDate.getTime() === today.getTime();
      }).length;
      
      const total = noStateCount + pendingCount;
      
      return {
        totalPending: total,
        totalApprovedToday: approvedToday,
        totalRejectedToday: rejectedToday
      };
    } catch (error) {
      console.error('Error fetching project documentation stats:', error);
      return {
        totalPending: 0,
        totalApprovedToday: 0,
        totalRejectedToday: 0
      };
    }
  },

  getUserProjectDocumentations: async (userId: number | string): Promise<any[]> => {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }
      const response = await axios.get(`/preliminary-project/all/${userId}`, getAuthHeaders());
      return response.data.data || response.data || [];
    } catch (error) {
      console.error('Error fetching user project documentations:', error);
      throw error;
    }
  },

  deleteProjectDocumentation: async (id: string | number): Promise<any> => {
    try {
      const response = await axios.delete(`/preliminary-project/${id}`, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error deleting project documentation:', error);
      throw error;
    }
  },

  deleteProjectDocumentationFile: async (fileId: string | number): Promise<any> => {
    try {
      const response = await axios.delete(`/preliminary-project/file/${fileId}`, getAuthHeaders());
      return response.data;
    } catch (error) {
      console.error('Error deleting project documentation file:', error);
      throw error;
    }
  }
};

